import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  TextField, 
  InputAdornment, 
  Divider, 
  Link,
  Paper
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Search as SearchIcon, 
  Storage as DbIcon, 
  Email as OutreachIcon, 
  Settings as SettingsIcon, 
  Map as MapIcon,
  Launch as LaunchIcon
} from '@mui/icons-material';

interface RouteItem {
  name: string;
  path: string;
  description: string;
}

interface RouteGroup {
  title: string;
  icon: React.ReactNode;
  color: string;
  items: RouteItem[];
}

const SitemapPage: React.FC = () => {
  const [search, setSearch] = useState('');

  const groups: RouteGroup[] = [
    {
      title: 'Database Directories',
      icon: <DbIcon sx={{ color: '#0284c7' }} />,
      color: '#0284c7',
      items: [
        { name: 'Companies Directory', path: '/database/companies', description: 'Central listing of all crawled company domains, websites, and metadata.' },
        { name: 'People & Contacts', path: '/database/people', description: 'Database of key decision makers, emails, titles, and company roles.' },
        { name: 'Industry Taxonomy Tags', path: '/database/industry-tags', description: 'Direct inline editor for Gemini AI cannabis taxonomy guidelines and active tag rules.' },
        { name: 'Verification Statuses', path: '/database/verification', description: 'Verification status mapping and active domain vetting logs.' },
        { name: 'US States Registry', path: '/database/states', description: 'Lookup database of United States codes, names, and regional parameters.' },
        { name: 'Countries Directory', path: '/database/countries', description: 'Global country registry used for international data classification.' },
        { name: 'Database Statistics', path: '/database/statistics', description: 'Aggregated analytics and metrics overview of your current database state.' },
        { name: 'System Audit Logs', path: '/database/logs', description: 'Full system execution history, API triggers, and database audit logs.' }
      ]
    },
    {
      title: 'Outreach & Workflow Engine',
      icon: <OutreachIcon sx={{ color: '#10b981' }} />,
      color: '#10b981',
      items: [
        { name: 'Outreach Home', path: '/outreach/home', description: 'The hub dashboard to kick off new batches, upload leads, or view active tasks.' },
        { name: 'AI Grounding Search', path: '/outreach/search', description: 'Deep grounding domain search tool connected directly to Google & Gemini.' },
        { name: 'Saved Batches', path: '/outreach/saved-results', description: 'Browse and export previously executed and saved outreach lists.' },
        { name: 'Import Leads Upload', path: '/outreach/upload', description: 'Upload raw contact lists, CSV payloads, or list exports for processing.' },
        { name: 'Domain Surf Processor', path: '/outreach/process', description: 'The real-time match selector board where you review candidate websites.' },
        { name: 'Domain List Manager', path: '/outreach/domain-list', description: 'Review, modify, or manually veto selected domains and their status.' },
        { name: 'Email Validation Board', path: '/outreach/validation', description: 'Verifies emails for deliverability using API-driven validators.' },
        { name: 'Clean Outreach Results', path: '/outreach/email-results', description: 'Final aggregated contact outputs ready to send to mailers or export.' },
        { name: 'Scheduled Tasks', path: '/outreach/scheduled-tasks', description: 'Manage and monitor recurring or background scraping cron jobs.' }
      ]
    },
    {
      title: 'Platform Settings & Admins',
      icon: <SettingsIcon sx={{ color: '#6366f1' }} />,
      color: '#6366f1',
      items: [
        { name: 'Profile Configuration', path: '/settings/profile', description: 'Manage your active administrator profile credentials and preferences.' },
        { name: 'User Management & Roles', path: '/settings/users', description: 'Review user login accounts, edit permissions, and verify access keys.' },
        { name: 'Database Configuration', path: '/settings/database-config', description: 'Configure active database connection strings, ports, and credentials.' },
        { name: 'External APIs & Keys', path: '/settings/apis', description: 'Integrate active API keys for Reoon, Hunter, Google, and email networks.' },
        { name: 'Gemini LLM Settings', path: '/settings/search-llm', description: 'Configure prompts, temperatures, and models for the AI search engine.' },
        { name: 'Taxonomy Settings Backup', path: '/settings/industry-tags', description: 'System-wide settings panel to view and audit industry tags.' }
      ]
    }
  ];

  const filteredGroups = groups.map(group => {
    const items = group.items.filter(item => {
      const q = search.toLowerCase().trim();
      return (
        item.name.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.path.toLowerCase().includes(q)
      );
    });
    return { ...group, items };
  }).filter(group => group.items.length > 0);

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4, py: 2 }}>
      {/* Header Banner */}
      <Paper elevation={0} sx={{ 
        p: 4, 
        borderRadius: 3, 
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Abstract background shape for rich aesthetics */}
        <Box sx={{ 
          position: 'absolute', 
          right: '-5%', 
          top: '-20%', 
          width: '300px', 
          height: '300px', 
          borderRadius: '50%', 
          background: 'rgba(56, 189, 248, 0.08)', 
          filter: 'blur(40px)' 
        }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <MapIcon sx={{ fontSize: '2.5rem', color: '#38bdf8' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
            EMS Platform Directory
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', maxWidth: '600px', fontSize: '1.05rem' }}>
          Welcome to your complete platform sitemap. Every single tab, settings panel, and workflow directory has its own dedicated URL path. Search or browse to jump directly to any workspace.
        </Typography>
      </Paper>

      {/* Instant Search Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <TextField
          placeholder="Filter directories, settings, and workflows by name or keywords..."
          variant="outlined"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: '1.5rem' }} />
              </InputAdornment>
            ),
          }}
          sx={{ 
            maxWidth: 800, 
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)',
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
        />
      </Box>

      {/* Directory Grid */}
      <Grid container spacing={3.5}>
        {filteredGroups.map((group, gIdx) => (
          <Grid item xs={12} md={4} key={gIdx}>
            <Card sx={{ 
              height: '100%', 
              borderRadius: 3, 
              boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                p: 2.5, 
                pb: 1.5, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5, 
                borderBottom: '1px solid', 
                borderColor: 'divider',
                backgroundColor: 'rgba(0, 0, 0, 0.015)'
              }}>
                {group.icon}
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  {group.title}
                </Typography>
              </Box>
              <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {group.items.map((item, idx) => (
                  <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Link 
                      component={RouterLink} 
                      to={item.path} 
                      sx={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: 0.75, 
                        color: 'primary.main', 
                        fontWeight: 600, 
                        textDecoration: 'none',
                        fontSize: '0.95rem',
                        width: 'fit-content',
                        '&:hover': { 
                          color: '#0284c7',
                          textDecoration: 'underline',
                          '& svg': { transform: 'translate(1px, -1px)' }
                        }
                      }}
                    >
                      {item.name}
                      <LaunchIcon sx={{ fontSize: '0.85rem', transition: 'transform 0.15s ease' }} />
                    </Link>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25, fontWeight: 500, fontFamily: 'monospace' }}>
                      {item.path}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.45 }}>
                      {item.description}
                    </Typography>
                    {idx < group.items.length - 1 && <Divider sx={{ mt: 2 }} />}
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SitemapPage;
